import { useState, useEffect } from 'preact/hooks'
import { getAvailableGenders, getAvailableSizeTypes } from './SizeFilterUtils'

export const UniversalSizeFilter = ({ values, onFilter }) => {
  const [activeGender, setActiveGender] = useState('')
  const [activeSizeType, setActiveSizeType] = useState('')

  const parsedSizes = parseSizeData(values)
  const { universalSizes, nonUniversalSizes } = parsedSizes

  const availableGenders = getAvailableGenders(universalSizes)
  const availableSizeTypes = getAvailableSizeTypes(universalSizes)

  useEffect(() => {
    if (availableGenders.length > 0) {
      if (!activeGender || !availableGenders.includes(activeGender)) {
        setActiveGender(availableGenders[0])
      }
    }
  }, [availableGenders, activeGender])

  useEffect(() => {
    const preferredSizeRange = localStorage.getItem('preferred_size_range') || 'EU'
    if (availableSizeTypes.includes(preferredSizeRange)) {
      setActiveSizeType(preferredSizeRange)
    } else if (availableSizeTypes.length > 0) {
      setActiveSizeType(availableSizeTypes[0])
    }
  }, [availableSizeTypes])

  const handleSizeTypeClick = (sizeType) => {
    setActiveSizeType(sizeType)
    localStorage.setItem('preferred_size_range', sizeType)
  }

  const handleGenderClick = (gender) => {
    setActiveGender(gender)
  }

  return (
    <div className="flex flex-col gap-y-1">
      {nonUniversalSizes.length > 0 && (
        <div className="flex flex-wrap items-center gap-0.5 w-full">
          {nonUniversalSizes.map((item) => (
            <SizeGridItem key={item.value} item={item} onFilter={onFilter} />
          ))}
        </div>
      )}

      {Object.keys(universalSizes).length > 0 && (
        <div className="flex flex-col gap-y-1">
          {availableGenders.length > 0 && (
            <div className="flex items-center w-full text-center bg-neutral-light p-0.25">
              {availableGenders.map((gender) => (
                <div
                  key={gender}
                  className={`flex items center justify-center w-full min-h-[36px] p-0.625 link-fade ${
                    activeGender === gender ? 'bg-brand-white' : ''
                  }`}
                  onClick={() => handleGenderClick(gender)}
                >
                  <a className="flex text-s font-bold">
                    {gender === 'WOMENS' ? 'Womens' : gender === 'MENS' ? 'Mens' : gender.toLowerCase()}
                  </a>
                </div>
              ))}
            </div>
          )}

          {availableSizeTypes.length > 0 && (
            <div className="flex items-center w-full text-center bg-neutral-light p-0.25">
              {availableSizeTypes.map((sizeType) => (
                <div
                  key={sizeType}
                  className={`flex items center justify-center w-full min-h-[36px] p-0.625 link-fade ${
                    activeSizeType === sizeType ? 'bg-brand-white' : ''
                  }`}
                  onClick={() => handleSizeTypeClick(sizeType)}
                >
                  <a className="flex text-s font-bold">
                    {sizeType === 'EU' ? 'EU' : sizeType}
                  </a>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-0.5 w-full">
            {universalSizes[activeGender]?.[activeSizeType]?.map((item) => (
              <SizeGridItem key={item.value} item={item} onFilter={onFilter} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const SizeGridItem = ({ item, onFilter }) => {
  return (
    <div
      className={`
        btn btn-primary btn-primary-alt btn-border btn-m w-full max-w-grid-1/4
        ${item.checked ? 'active' : ''}
      `}
      onClick={() => onFilter(item.value)}
    >
      <input
        type="checkbox"
        checked={item.checked}
        onChange={() => onFilter(item.value)}
        className="sr-only"
        aria-label={item.displayLabel || item.label}
      />
      <span>{item.size || item.label}</span>
    </div>
  )
}

const parseSizeData = (values) => {
  const universalSizes = {}
  const nonUniversalSizes = []

  values.forEach((item) => {
    const isAvailable = !item.disabled && (item.count === undefined || item.count > 0)

    if (!isAvailable) return

    const sizingString = item.label
    const sizeObject = {}

    if (sizingString?.includes(',') || sizingString?.includes('/')) {
      const separator = sizingString.includes('/') ? '/' : ','
      const sizingArrayWithDigits = sizingString.split(separator).filter(val => val.match(/\d+/))

      sizingArrayWithDigits.forEach(val => {
        val = val.replace('NULL', '').replace('  ', ' ').trim()
        const splitVal = val.split(' ')
        const size = parseFloat(splitVal.pop())
        const sizeTitle = splitVal.join(' ').trim().toUpperCase()

        if (!isNaN(size) && size > 0) {
          sizeObject[sizeTitle] = {
            size,
            value: item.value,
            count: item.count,
            ...item
          }
        }
      })

      delete sizeObject.CM

      let genderObject = null

      if (sizeObject['WOMENS US']) {
        genderObject = {}
        genderObject.WOMENS = {
          US: sizeObject['WOMENS US'],
          UK: sizeObject['WOMENS UK'],
          EU: sizeObject.EURO
        }
      } else if (sizeObject['MENS US']) {
        genderObject = {}
        genderObject.MENS = {
          US: sizeObject['MENS US'],
          UK: sizeObject['MENS UK'],
          EU: sizeObject.EURO
        }
      } else if (sizeObject['KIDS US']) {
        genderObject = {}
        genderObject.KIDS = {
          US: sizeObject['KIDS US'],
          UK: sizeObject['KIDS UK'],
          EU: sizeObject.EURO
        }
      }

      if (genderObject) {
        Object.keys(genderObject).forEach(gender => {
          if (!universalSizes[gender]) {
            universalSizes[gender] = {}
          }

          Object.keys(genderObject[gender]).forEach(sizeType => {
            if (!universalSizes[gender][sizeType]) {
              universalSizes[gender][sizeType] = []
            }

            const sizeData = genderObject[gender][sizeType]
            if (sizeData) {
              universalSizes[gender][sizeType].push({
                ...sizeData,
                sizeType,
                displayLabel: `${sizeType === 'EU' ? 'EU' : sizeType} ${sizeData.size}`
              })
            }
          })
        })
      }
    } else {
      // Non-universal size
      nonUniversalSizes.push(item)
    }
  })

  // Sort sizes numerically within each gender/type
  Object.keys(universalSizes).forEach(gender => {
    Object.keys(universalSizes[gender]).forEach(sizeType => {
      universalSizes[gender][sizeType].sort((a, b) => {
        return parseFloat(a.size) - parseFloat(b.size)
      })
    })
  })

  return { universalSizes, nonUniversalSizes }
}
